import {
  DashboardLeagueInheritedTournatedFeeVat,
  LicensePaymentStatus,
  PurchasedLicensePaymentMethod,
  TournatedFeeVatSource as ApiTournatedFeeVatSource,
} from "@/api/__generated__/schemas";
import {
  FeeMode as ApiFeeMode,
  FeeType as ApiFeeType,
  ServiceType as ApiServiceType,
} from "@/graphql";
import { mapToCurrencyCode } from "@/mappers/common.mappers";
import {
  CommonFeesSegment,
  FeeBasis,
  FeesSegment,
  FeeType,
  LicensePaymentMethod,
  PaymentMethod,
  PaymentStatus,
  InheritedTournatedFeeVat,
  ServiceType,
  TournatedFeeVat,
  TournatedFeeVatSource,
} from "@/types";

export const mapToPaymentMethod = (apiPaymentMethod: string): PaymentMethod => {
  switch (apiPaymentMethod.toLowerCase()) {
    case "card":
    case "bank transfer":
    case "bank_transfer":
      return "bankTransfer";
    case "cash":
      return "cash";
    case "free":
      return "free";
    case "online":
    case "online transfer":
    case "online_transfer":
      return "online";
    case "invoice":
      return "invoice";
    case "wechat":
    case "wechat pay":
    case "wechat_pay":
    case "wechatpay":
    case "qrcode":
    case "qr_code":
      return "wechat";
    default:
      throw new Error(`Unexpected payment method: "${apiPaymentMethod}"`);
  }
};

export const mapToLicensePaymentMethod = (
  method: PurchasedLicensePaymentMethod
): LicensePaymentMethod => {
  switch (method) {
    case PurchasedLicensePaymentMethod.BANK_TRANSFER:
      return "bankTransfer";
    case PurchasedLicensePaymentMethod.CASH:
      return "cash";
    case PurchasedLicensePaymentMethod.FREE:
      return "free";
    case PurchasedLicensePaymentMethod.ONLINE:
      return "online";
  }
};

export const mapToApiPaymentMethod = (
  method: LicensePaymentMethod
): PurchasedLicensePaymentMethod => {
  switch (method) {
    case "bankTransfer":
      return PurchasedLicensePaymentMethod.BANK_TRANSFER;
    case "cash":
      return PurchasedLicensePaymentMethod.CASH;
    case "free":
      return PurchasedLicensePaymentMethod.FREE;
    case "online":
      return PurchasedLicensePaymentMethod.ONLINE;
  }
};

export const mapToPaymentStatus = (apiStatus: string | null | undefined): PaymentStatus | null => {
  switch (apiStatus?.toLowerCase()) {
    case "paid":
    case "completed":
      return "completed";
    case "expired":
      return "expired";
    case "refunded":
      return "refunded";
    case "canceled":
      return "canceled";
    case "pending":
      return "pending";
    case "partially_paid":
    case "partiallypaid":
      return "partiallyPaid";
    case "failed":
      return "failed";
    default:
      // The backend now serializes unrecognized DB values as null; treat anything
      // we don't recognize (including null/undefined) as "no status" rather than throwing.
      return null;
  }
};

export const mapToApiPaymentStatus = (status: PaymentStatus): LicensePaymentStatus => {
  switch (status) {
    case "completed":
      return LicensePaymentStatus.COMPLETED;
    case "expired":
      return LicensePaymentStatus.EXPIRED;
    case "refunded":
      return LicensePaymentStatus.REFUNDED;
    case "canceled":
      return LicensePaymentStatus.CANCELED;
    case "pending":
      return LicensePaymentStatus.PENDING;
    case "partiallyPaid":
      throw new Error(
        `Cannot map "partiallyPaid" to LicensePaymentStatus — license endpoints do not accept partial-paid status`
      );
    case "failed":
      return LicensePaymentStatus.FAILED;
    default:
      throw new Error(`Unexpected payment status: "${status}"`);
  }
};

export const mapToFeeType = (apiFeeType: string): FeeType => {
  switch (apiFeeType) {
    case "FIXED":
      return "fixed";
    case "PERCENTAGE":
      return "percentage";
    default:
      return "fixed";
  }
};

export const mapToServiceType = (apiServiceType: string): ServiceType => {
  switch (apiServiceType) {
    case "ENTRY":
      return "entry";
    case "LICENSE":
      return "license";
    default:
      return "entry";
  }
};

export const mapToApiFeeType = (feeType: FeeType): ApiFeeType => {
  return feeType === "fixed" ? ApiFeeType.Fixed : ApiFeeType.Percentage;
};

export const mapToApiServiceType = (serviceType: ServiceType): ApiServiceType => {
  return serviceType === "entry" ? ApiServiceType.Entry : ApiServiceType.License;
};

export const mapToApiFeeMode = (feeBasis: string): ApiFeeMode => {
  return feeBasis === "perPlayer" ? ApiFeeMode.PerPlayer : ApiFeeMode.PerEntry;
};

/** Maps the API fee mode (`PER_ENTRY` / `PER_PLAYER`) to the domain fee basis. */
export const mapToFeeBasis = (apiFeeMode: string | null | undefined): FeeBasis => {
  return apiFeeMode === "PER_PLAYER" ? "perPlayer" : "perEntry";
};

/** Maps internal range end (-1 = unlimited) to API value (100000 = unlimited). */
export const mapRangeEndToApi = (value: number | null | undefined): number => {
  if (value === -1 || value == null) return 100000;
  return value;
};

/** Maps API range end (100000 = unlimited) to internal value (-1 = unlimited). */
export const mapRangeEndFromApi = (value: number | null | undefined): number => {
  if (value === 100000 || value == null) return -1;
  return value;
};
export type FeesSegmentInput = {
  id: number;
  tournatedFee?: number | null;
  tournatedMaxFee?: number | null;
  tournatedMinFee?: number | null;
  tournatedMinPercentageFee?: number | null;
  tournatedMaxPercentageFee?: number | null;
  partnerFee?: number | null;
  partnerMinFee?: number | null;
  partnerMaxFee?: number | null;
  partnerMinPercentageFee?: number | null;
  partnerMaxPercentageFee?: number | null;
  currency?: string | null;
  rangeStart: number;
  rangeEnd: number;
  serviceType: string;
  feeType: string;
  feeMode?: string | null;
  isMonetization?: boolean | null;
};

export const mapToFeesSegment = (fragment: FeesSegmentInput): FeesSegment => {
  const common: CommonFeesSegment = {
    id: fragment.id,
    range: {
      from: fragment.rangeStart,
      to: mapRangeEndFromApi(fragment.rangeEnd),
    },
    currency: mapToCurrencyCode(fragment.currency ?? "EUR") ?? "EUR",
    serviceType: mapToServiceType(fragment.serviceType!),
    feeBasis: mapToFeeBasis(fragment.feeMode),
    enableMonetization: fragment.isMonetization ?? false,
  };

  const feeType = mapToFeeType(fragment.feeType!);

  switch (feeType) {
    case "fixed":
      return {
        ...common,
        feeType: FeeType.FIXED,
        tournated: {
          value: fragment.tournatedFee ?? null,
          minPercentage: fragment.tournatedMinPercentageFee ?? null,
          maxPercentage: fragment.tournatedMaxPercentageFee ?? null,
        },
        partner: {
          value: fragment.partnerFee ?? null,
          minPercentage: fragment.partnerMinPercentageFee ?? null,
          maxPercentage: fragment.partnerMaxPercentageFee ?? null,
        },
      };
    case "percentage":
      return {
        ...common,
        feeType: FeeType.PERCENTAGE,
        tournated: {
          minValue: fragment.tournatedMinFee ?? null,
          maxValue: fragment.tournatedMaxFee ?? null,
          percentageValue: fragment.tournatedFee ?? 0,
        },
        partner: {
          minValue: fragment.partnerMinFee ?? null,
          maxValue: fragment.partnerMaxFee ?? null,
          percentageValue: fragment.partnerFee ?? 0,
        },
      };
  }
};

/**
 * REST VAT-on-Tournated-fee columns (platform / league detail, federation
 * `customFeeVat` entry) → domain {@link TournatedFeeVat}. Reads defensively so a
 * backend that predates the fields reads as "VAT off, default rate".
 */
export const mapToTournatedFeeVat = (input: {
  isTournatedFeeVatEnable?: boolean | null;
  tournatedFeeVat?: number | null;
}): TournatedFeeVat => ({
  isEnabled: input.isTournatedFeeVatEnable === true,
  rate: input.tournatedFeeVat ?? null,
});

const TOURNATED_FEE_VAT_SOURCE_MAP: { [key in ApiTournatedFeeVatSource]: TournatedFeeVatSource } = {
  FEDERATION: "federation",
  PLATFORM: "platform",
  NONE: "none",
};

/**
 * REST league `inheritedTournatedFeeVat` → domain. `null` / absent (no
 * `LEAGUE_FEE:READ`, or a backend predating the projection) stays `null`.
 */
export const mapToInheritedTournatedFeeVat = (
  input: DashboardLeagueInheritedTournatedFeeVat | null | undefined
): InheritedTournatedFeeVat | null =>
  input
    ? {
        ...mapToTournatedFeeVat(input),
        source: TOURNATED_FEE_VAT_SOURCE_MAP[input.source] ?? "none",
      }
    : null;
